import {
  Callout,
  Figure,
  H2,
  H3,
  LI,
  P,
  Table,
  UL,
} from "../../components/blog/BlogTypography";

import rootDirectory from "../../assets/blog/licindia-meduzalocker-breach/fig1-root-directory.png";
import infoPage from "../../assets/blog/licindia-meduzalocker-breach/fig2-info-page.png";
import financialDirectory from "../../assets/blog/licindia-meduzalocker-breach/fig3-financial-directory.png";
import clientsDirectory from "../../assets/blog/licindia-meduzalocker-breach/fig4-clients-directory.png";
import legalDirectory from "../../assets/blog/licindia-meduzalocker-breach/fig5-legal-directory.png";
import reportsDirectory from "../../assets/blog/licindia-meduzalocker-breach/fig6-reports-directory.png";

const SNAPSHOT_HEADERS = ["Observed", "What the DLS post shows"];
const SNAPSHOT_ROWS = [
  ["Threat-actor interface", "MeduzaLocker DLS with victim “Licindia.”"],
  [
    "Domain attribution on actor page",
    "The Info page states: “Organization with 9 emails extracted. Domain: licindia.com.”",
  ],
  [
    "Repository structure",
    "Financial, HR, Legal, Marketing, Reports, Unsorted, plus a captured Clients directory.",
  ],
  [
    "Explicit platform counts",
    "Financial 2,836; Marketing 415; Unsorted 324; Legal 47; Reports 30.",
  ],
  ["Publication date", "2 September 2026."],
];

const COUNTS_HEADERS = ["Directory", "Items", "Evidence note"];
const COUNTS_ROWS = [
  ["Financial", "2,836", "29 pages shown by the interface"],
  ["Marketing", "415", "5 pages"],
  ["Unsorted", "324", "4 pages"],
  ["Legal", "47", "Demo page shows 10 of 47"],
  ["Reports", "30", "Demo page shows 10 of 30"],
];

export default function LicindiaMeduzaLockerClaim() {
  return (
    <>
      <Callout>
        Analysis based on a threat-actor data-leak-site listing and its exposed demo interface.
        Filenames and platform-reported counts are described as displayed by the actor's own
        disclosure platform and have not been independently validated. No personal records, sample
        documents, or download locations are reproduced in this post.
      </Callout>

      <P>
        CyberMon identified a data-leak-site (DLS) listing operated under the MeduzaLocker name
        that contains a victim entry labelled "Licindia," associates it with the domain
        licindia.com, and exposes a categorized file repository spanning Financial, HR, Legal,
        Marketing, Reports, Clients, and Unsorted data. The data is listed for sale at $50,000. The
        domain and naming pattern are consistent with the Life Insurance Corporation of India
        (LIC), the country's dominant state-owned life insurer, though the DLS listing itself does
        not independently prove that the underlying data originated from LIC's infrastructure.
      </P>

      <H2>Evidence Snapshot</H2>
      <Table headers={SNAPSHOT_HEADERS} rows={SNAPSHOT_ROWS} />

      <H2>A Structured Repository, Not a Single Sample</H2>
      <P>
        The strongest technical characteristic of the listing is its organization. Rather than a
        single archive or a flat credential dump, the repository is divided by business function.
        This matters because a cross-functional dataset can carry more intelligence value than
        isolated files: financial records can potentially be correlated with client, procurement,
        HR, tax, and operational data.
      </P>
      <P>
        The evidence does not show where these files were originally stored. Nothing in the DLS
        post establishes whether the source was a workstation, shared drive, file server,
        accounting application, cloud repository, backup system, Nextcloud deployment, third-party
        environment, or another data source.
      </P>

      <Figure
        src={rootDirectory}
        alt="Screenshot of the MeduzaLocker DLS root directory listing for 'Licindia', showing a demo-access banner and eight top-level folders: Clients, Confidential, Financial, HR, Legal, Marketing, Reports, and Unsorted."
        caption="Figure 1: Root directory view from the MeduzaLocker DLS post."
      />

      <H2>Platform-Reported File Counts</H2>
      <Table headers={COUNTS_HEADERS} rows={COUNTS_ROWS} />
      <P>
        The five explicitly quantified categories account for 3,652 listed items before Clients
        and HR are considered.
      </P>

      <Figure
        src={infoPage}
        alt="Screenshot of the MeduzaLocker actor-controlled Info page for 'Licindia', showing a Company panel with Name: Licindia, Address: em-dash, Description: 'Organization with 9 emails extracted. Domain: licindia.com', and Domain: licindia.com."
        caption="Figure 2: The actor-controlled Info page labels the entry “Licindia” and associates it with licindia.com."
      />

      <H2>Financial: 2,836 Listed Items</H2>
      <P>
        The Financial directory is the largest explicitly quantified section. Visible spreadsheet
        filenames combine organization or trading names with structured identifier strings,
        including examples associated with AQUA ROYAL, AQUA TRENDS, ARUNA AGENCY, and ASN HYDRO.
      </P>

      <Figure
        src={financialDirectory}
        alt="Screenshot of the MeduzaLocker Financial directory listing for 'Licindia', showing reconciliation-style spreadsheet filenames combining organization names with structured identifiers, alongside a demo-access banner."
        caption="Figure 3: Financial directory sample showing reconciliation-style spreadsheet filenames and the demo-access banner. Filenames are reported as displayed; underlying spreadsheet contents were not validated."
      />

      <P>
        Without opening and validating the underlying spreadsheets, CyberMon cannot determine
        which fields they contain or whether every identifier is genuine. If authentic, however,
        combinations of business names, registration-style identifiers, and reconciliation records
        could increase the credibility of vendor impersonation, payment-redirection, or targeted
        social-engineering attempts.
      </P>

      <H2>Clients: Purchase-Order CSVs and Output Data</H2>
      <P>
        The Clients directory contains multiple CSV files beginning with "FBP Purchase Order,"
        carrying sequential identifiers and date/time components in their filenames. The interface
        exposes both Open and Download actions for these files, and a separate OUTPUT.xls is also
        listed.
      </P>

      <Figure
        src={clientsDirectory}
        alt="Screenshot of the MeduzaLocker Clients directory listing for 'Licindia', showing multiple purchase-order-labelled CSV files with sequential identifiers, an OUTPUT.xls file, and Open and Download actions exposed by the interface."
        caption="Figure 4: Clients directory sample showing purchase-order-labelled CSV files with Open and Download actions exposed by the interface."
      />

      <P>
        Purchase-order context can be operationally sensitive because it may reveal supplier
        relationships, transaction workflows, or document patterns. The captures do not prove that
        such information has been used in fraud; they only show the listed filenames and
        file-access routes.
      </P>

      <H2>Legal: GST and GSTR1-Labeled Documents</H2>
      <P>
        The Legal directory contains filenames referencing GST and GSTR1, including multiple PDFs
        with structured identifiers and accounting periods such as 032023, 122022, 082022, 112022,
        012023, and 062022.
      </P>

      <Figure
        src={legalDirectory}
        alt="Screenshot of the MeduzaLocker Legal directory listing for 'Licindia', showing GST- and GSTR1-labelled PDF filenames with accounting-period identifiers and Open/Download actions."
        caption="Figure 5: Legal directory sample containing GST- and GSTR1-labelled PDF filenames. The screenshot supports the filenames and interface metadata only, not the contents of the documents."
      />

      <P>
        The filenames support describing these as GST/GSTR1-labeled documents. They do not
        establish which taxpayer fields, personal data, or financial values are present inside the
        documents.
      </P>

      <H2>HR: ESI-Labeled Material</H2>
      <P>
        The HR directory includes a PDF named "ESI 51001438980000999C11.pdf," with Open and
        Download actions exposed by the interface.
      </P>

      <H2>Reports: Historical Microsoft Access Databases</H2>
      <P>
        The Reports directory includes multiple Microsoft Access database files: converted.mdb
        (38.8 MB), main1819.mdb (9.4 MB), main1920.mdb (10.7 MB), main2021.mdb (11.9 MB),
        main2122.mdb (14.6 MB), and main2223.mdb (15.1 MB).
      </P>

      <Figure
        src={reportsDirectory}
        alt="Screenshot of the MeduzaLocker Reports directory listing for 'Licindia', showing multiple .mdb Microsoft Access database files named by year range alongside spreadsheet, PDF, and XML artifacts."
        caption="Figure 6: Reports directory sample showing multiple .mdb Microsoft Access database files alongside spreadsheet, PDF, and XML artifacts. Database schemas and record counts were not available in the DLS post."
      />

      <P>
        The year-like naming pattern suggests a historical sequence, but the captures contain no
        database schemas, table names, or row counts. A single database can contain multiple
        related tables, so record counts cannot be inferred from file size alone.
      </P>

      <H2>Marketing and Unsorted: Operational Artifacts Mixed Across Categories</H2>
      <P>
        The Marketing directory reports 415 items and contains numerous spreadsheets. Visible
        names include "01.MONTHILY GST RECONILATION APRIL 22 ONWARDS.xlsx," "stock.xlsx," and
        "Sundry Creditor.xlsx." Another page lists "Welcome to Nextcloud Hub.docx" and "winGlobal
        Sign.cer." This mix suggests that the actor's directory labels may not precisely reflect
        business ownership or data sensitivity.
      </P>
      <P>
        The Unsorted directory reports 324 items and includes legacy .XLS files such as
        01SLXL.XLS, 01SR1.XLS, and 01SR2.XLS. Legacy spreadsheet and Access formats can contain
        substantial structured data even when their filenames are generic.
      </P>

      <H2>Technical Characteristics of the Disclosure Platform</H2>
      <P>
        The captured HTML reveals a browsable disclosure application rather than a static victim
        notice. Individual file entries expose routes following patterns such as
        /preview/sorted/&lt;category&gt;/&lt;filename&gt; and
        /download/sorted/&lt;category&gt;/&lt;filename&gt;. CSV and some supported files
        additionally expose /view/ actions. The platform implements pagination, with Marketing
        spread across five pages and Financial across 29 pages.
      </P>
      <P>
        The DLS also repeatedly displays demo-access language indicating that only a subset of
        files is initially visible and that fuller access would be available after publication.
        This means the DLS post is best treated as evidence of a staged data-disclosure mechanism
        rather than a complete copy of the underlying dataset.
      </P>

      <H2>What the Timestamps Do — and Do Not — Tell Us</H2>
      <P>
        Many displayed file "Modified" timestamps cluster around 17-18 August 2026, while root
        directory timestamps in one capture show 19 August 2026. The DLS post appeared on 2
        September 2026. These dates establish what the interface displayed at capture time.
      </P>
      <P>
        They do not establish the breach date, initial compromise date, exfiltration time,
        ransomware execution time, or when the organization first became aware of the incident.
        The displayed timestamps could represent source-file metadata, ingestion timing,
        repository processing, or another internal workflow of the disclosure platform.
      </P>

      <H2>Evidence Boundary: Confirmed vs. Unconfirmed</H2>

      <H3>Confirmed from the DLS post</H3>
      <UL>
        <LI>A Tor interface branded MeduzaLocker FILESTATION.</LI>
        <LI>A victim entry named "Licindia."</LI>
        <LI>
          The actor page associates the entry with licindia.com and states nine emails were
          extracted.
        </LI>
        <LI>A multi-directory repository organized by business-function categories.</LI>
        <LI>Platform-reported counts for Financial, Marketing, Unsorted, Legal, and Reports.</LI>
        <LI>Visible Preview / View / Download routes and demo-access restrictions.</LI>
      </UL>

      <H3>Not established by the DLS post</H3>
      <UL>
        <LI>The initial-access vector or exploited vulnerability.</LI>
        <LI>Whether ransomware encryption occurred inside the organization.</LI>
        <LI>The exact exfiltration volume or number of affected people.</LI>
        <LI>Whether every listed file originated directly from LIC infrastructure.</LI>
        <LI>Ransom amount, negotiations, or payment status.</LI>
        <LI>Independent confirmation by the affected organization.</LI>
        <LI>Any secondary fraud or weaponization of the exposed data.</LI>
      </UL>

      <H2>Implications if the Data Is Authentic</H2>
      <UL>
        <LI>
          <strong>Business email compromise and invoice fraud:</strong> Purchase orders, creditor
          records, and financial context could potentially improve the realism of
          supplier-impersonation or payment-redirection attempts.
        </LI>
        <LI>
          <strong>Third-party exposure:</strong> Files named after external entities may indicate
          supplier, customer, or partner data that could require separate impact analysis.
        </LI>
        <LI>
          <strong>Identity and social engineering:</strong> HR, tax, and regulatory records may
          contain attributes useful for highly targeted social engineering.
        </LI>
        <LI>
          <strong>Historical structured-data exposure:</strong> The sequence of .mdb files could
          represent multi-year datasets. If validated, historical retention would materially
          affect incident scope.
        </LI>
        <LI>
          <strong>Credential and secret review:</strong> The listing of email addresses, a
          certificate file, and infrastructure-adjacent artifacts warrants investigation for
          passwords, tokens, private keys, browser stores, configuration secrets, or archived
          credentials.
        </LI>
      </UL>

      <H2>Incident-Response Priorities</H2>
      <UL>
        <LI>
          <strong>Validate authenticity:</strong> Compare sample filenames, hashes where
          available, document naming conventions, and internal file inventories against
          authoritative systems.
        </LI>
        <LI>
          <strong>Establish provenance:</strong> Identify the servers, shares, applications,
          endpoints, or repositories that originally hosted the relevant Financial, Legal,
          Marketing, Reports, HR, and Client material.
        </LI>
        <LI>
          <strong>Hunt for exfiltration:</strong> Review endpoint, identity, proxy, firewall,
          cloud, and storage telemetry for bulk reads, archive creation, staging, compression, and
          unusual outbound transfers.
        </LI>
        <LI>
          <strong>Scope identities and third parties:</strong> Determine whether the affected
          corpus contains employee, customer, vendor, partner, or other third-party information.
        </LI>
        <LI>
          <strong>Review secrets and authentication artifacts:</strong> Search the implicated
          repositories for credentials, tokens, browser data, private keys, certificate material,
          connection strings, and configuration files.
        </LI>
        <LI>
          <strong>Monitor for secondary abuse:</strong> Track phishing, impersonation, supplier
          fraud, leaked credentials, and other activity that may reuse genuine business context
          from the exposed files.
        </LI>
      </UL>

      <H2>CyberMon Assessment</H2>
      <Callout>
        <strong>
          The DLS post documents a technically significant MeduzaLocker data-exposure claim
          involving "Licindia" and licindia.com.
        </strong>{" "}
        The strongest signal is the combination of a structured multi-directory repository,
        thousands of platform-reported files, business-specific filenames, historical database
        files, purchase-order CSVs, and tax-related documents.
      </Callout>
      <P>
        At the same time, the captures do not independently prove a compromise of LIC
        infrastructure, identify the attack vector, establish ransomware execution, quantify the
        actual exfiltrated dataset, or determine the number of affected individuals. Until those
        elements are independently validated, the most accurate characterization is a substantial
        MeduzaLocker data-exposure claim supported by a browsable file listing, with the
        underlying intrusion and complete dataset still requiring confirmation.
      </P>

      <Callout>
        <strong>Publication note:</strong> This blog is intended for defensive cybersecurity and
        threat-intelligence awareness. References to claimed file counts, categories, and naming
        patterns describe threat-actor assertions or the disclosure platform's own metadata unless
        explicitly identified as independently confirmed. No personal records, sample documents,
        or download locations are reproduced.
      </Callout>
    </>
  );
}
